import * as THREE from 'three';
import { Sky } from 'three/examples/jsm/objects/Sky.js';

// Physically-based atmosphere (three's Preetham Sky) plus a drifting fbm cloud
// dome, a fog-matched horizon band and a star field. One shared sun direction
// drives the atmosphere, the key light and the fog colour, so sky, haze and
// ground read as one environment instead of three unrelated layers.
// Ported from augmento-tower/src/three/sky.ts.

export interface SkyParams {
    turbidity: number;
    rayleigh: number;
    mieCoefficient: number;
    mieDirectionalG: number;
}

// Rayleigh carries the blue gradient; the tower's port ran it at 0 and let a
// painted band do that job, but this scene looks UP at the sky far more often,
// so the real scattering is worth having. Turbidity is high for a desert
// plateau: Riyadh air carries dust, which is what gives the sky its warm,
// slightly milky horizon.
export const DEFAULT_SKY: SkyParams = {
    turbidity: 3.2,
    rayleigh: 2.8,
    mieCoefficient: 0.005,
    mieDirectionalG: 0.8,
};

// Sky-only exposure: dims the HDR atmosphere without touching the model.
//
// Note on where this number comes from. The scene renders through an
// EffectComposer into a HalfFloat target, and three skips its tone-mapping
// chunk whenever the render target is not the canvas, so renderer.exposure
// never touches these pixels. Everything the backdrop paints is therefore
// authored in linear space and read back straight out: what you set is what
// ships, and this dial is the only thing standing between the Preetham
// atmosphere's HDR output and a clipped white sky.
const SKY_EXPOSURE = 1.1;

const CLOUD_VERTEX = /* glsl */ `
  varying vec3 vDir;
  void main() {
    vDir = normalize(position);
    gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
  }
`;

// Drifting fbm cumulus band. Rendered on an inner dome over the Sky shell;
// alpha-blended so the atmosphere stays visible through the gaps.
const CLOUD_FRAGMENT = /* glsl */ `
  uniform float uTime;
  uniform float uCoverage;
  uniform vec3 uColor;
  uniform float uOpacity;
  varying vec3 vDir;

  float chash(vec2 p) { return fract(sin(dot(p, vec2(127.1, 311.7))) * 43758.5453123); }
  float cnoise(vec2 p) {
    vec2 i = floor(p);
    vec2 f = fract(p);
    vec2 q = f * f * (3.0 - 2.0 * f);
    return mix(
      mix(chash(i), chash(i + vec2(1.0, 0.0)), q.x),
      mix(chash(i + vec2(0.0, 1.0)), chash(i + vec2(1.0, 1.0)), q.x),
      q.y
    );
  }
  float cfbm(vec2 p) {
    float v = 0.0;
    float amp = 0.5;
    for (int i = 0; i < 4; i++) {
      v += amp * cnoise(p);
      p *= 2.13;
      amp *= 0.5;
    }
    return v;
  }

  void main() {
    vec3 dir = normalize(vDir);
    if (dir.y < 0.02) discard;
    // Project onto a virtual cloud plane so clouds read flat overhead and
    // compress toward the horizon, then drift with time.
    vec2 uv = dir.xz / (dir.y + 0.15) * 0.6 + uTime * 0.019;
    // Higher base frequency than the tower's version: over a desert the sky
    // wants small scattered puffs, not the big overcast banks that read
    // correctly over a coastal city.
    float n = cfbm(uv * 3.4) + 0.5 * cfbm(uv * 8.1 + 3.7);
    float mask = smoothstep(1.0 - uCoverage, 1.0 - uCoverage + 0.22, n * 0.72);
    // Fade near the horizon so clouds never intersect the fog band.
    mask *= smoothstep(0.02, 0.18, dir.y);
    if (mask < 0.004) discard;
    gl_FragColor = vec4(uColor, mask * uOpacity);
  }
`;

const HORIZON_VERTEX = CLOUD_VERTEX;

// Tints the true horizon line with the scene's fog colour so the terrain
// dissolves into haze into sky with no seam.
//
// The fade is deliberately tight (gone by ~7 deg elevation). This viewer sits
// low over a table-top maquette, so the visible sky is almost entirely the
// 0-30 deg band: the tower's wider version of this band, tuned for a camera
// that looks up, covered the whole dome here and flattened the atmosphere into
// one grey wall.
const HORIZON_FRAGMENT = /* glsl */ `
  uniform vec3 uColor;
  varying vec3 vDir;
  void main() {
    vec3 dir = normalize(vDir);
    if (dir.y < -0.35) discard;
    float alpha = (1.0 - smoothstep(-0.02, 0.13, dir.y)) * smoothstep(-0.32, -0.2, dir.y);
    gl_FragColor = vec4(uColor, alpha * 0.72);
  }
`;

// Day white sits slightly above 1.0 (HDR) so clouds still read white against
// the bright sky instead of washing grey.
const CLOUD_DAY = new THREE.Color(1.2, 1.2, 1.24);
const CLOUD_DUSK = new THREE.Color('#f0a26b');
const CLOUD_NIGHT = new THREE.Color('#10151f');
// Well above 1.0: this has to clip to white after the final exposure, or the
// sun reads as a grey disc rather than as the sun.
const SUN_COLOR = new THREE.Color(14, 12.6, 10.2);

export interface SkyRig {
    readonly params: SkyParams;
    readonly clouds: { coverage: THREE.IUniform<number>; opacity: THREE.IUniform<number> };
    setSun(sunDir: THREE.Vector3): void;
    /**
     * The visible sun. Doubles as the god-rays light source, which is why it is
     * a real mesh rather than a shader term: GodRaysEffect masks the screen
     * against an actual object's silhouette.
     */
    readonly sun: THREE.Mesh;
    /**
     * daylight 0..1, dusk 0..1: tints the clouds white by day, ember at dusk,
     * near-black at night so they never glow against a dark sky, and fades the
     * stars in only once the sun is properly down.
     */
    setLighting(daylight: number, dusk: number): void;
    /** Fog-matched colour painted over the horizon band. */
    setHorizon(color: THREE.Color): void;
    applyParams(): void;
    update(dt: number): void;
    dispose(): void;
}

/**
 * @param radius Outer shell radius in scene units. Must sit beyond everything
 *   visible but inside the camera's far plane.
 */
export function createSky(scene: THREE.Scene, radius: number): SkyRig {
    const sky = new Sky();
    sky.scale.setScalar(radius);
    // No visible sun disc: the mie halo alone marks the sun, and a disc at this
    // HDR intensity blows a hole through the bloom pass.
    sky.material.onBeforeCompile = (shader) => {
        shader.fragmentShader = shader.fragmentShader
            .replace('L0 += ( vSunE * 19000.0 * Fex ) * sundisc;', '')
            .replace(
                'gl_FragColor = vec4( retColor, 1.0 );',
                `gl_FragColor = vec4( retColor * ${SKY_EXPOSURE.toFixed(3)}, 1.0 );`,
            );
    };
    sky.material.needsUpdate = true;
    sky.material.fog = false;
    const u = sky.material.uniforms;
    const params: SkyParams = { ...DEFAULT_SKY };

    const applyParams = () => {
        u.turbidity.value = params.turbidity;
        u.rayleigh.value = params.rayleigh;
        u.mieCoefficient.value = params.mieCoefficient;
        u.mieDirectionalG.value = params.mieDirectionalG;
    };
    applyParams();
    scene.add(sky);
    // The sky mesh is the backdrop; no flat colour or photo underneath it.
    scene.background = null;

    // Backdrop stack, all depthWrite-false and ordered by renderOrder:
    // atmosphere (0) -> horizon band (1) -> clouds (2) -> stars (3).
    const cloudUniforms = {
        uTime: { value: 0 },
        // Sparser than a temperate sky: this is a desert plateau.
        uCoverage: { value: 0.26 },
        uColor: { value: CLOUD_DAY.clone() },
        uOpacity: { value: 0.5 },
    };
    const cloudMaterial = new THREE.ShaderMaterial({
        vertexShader: CLOUD_VERTEX,
        fragmentShader: CLOUD_FRAGMENT,
        uniforms: cloudUniforms,
        transparent: true,
        depthWrite: false,
        side: THREE.BackSide,
        fog: false,
    });
    const clouds = new THREE.Mesh(new THREE.SphereGeometry(1, 32, 16), cloudMaterial);
    clouds.scale.setScalar(radius * 0.88);
    clouds.frustumCulled = false;
    clouds.renderOrder = 2;
    scene.add(clouds);

    // The sun itself. Transparent and depthWrite-false because GodRaysEffect
    // requires both: it renders this mesh into its own mask buffer and would
    // otherwise punch a hole in the scene's depth.
    const sunRadius = radius * 0.78;
    // Additive and genuinely hot. Both matter: at a merely "bright" 2.4 the disc
    // came out DARKER than its own god-ray halo, because the halo clips to white
    // through the final exposure while 2.4 lands around 0.6 grey, which read as a
    // hole punched in the middle of the glow. Additive blending also means the
    // disc can only ever add light, never replace what is behind it.
    const sunMaterial = new THREE.MeshBasicMaterial({
        color: SUN_COLOR.clone(),
        transparent: true,
        depthWrite: false,
        blending: THREE.AdditiveBlending,
        fog: false,
    });
    const sun = new THREE.Mesh(new THREE.SphereGeometry(radius * 0.011, 24, 16), sunMaterial);
    sun.frustumCulled = false;
    sun.renderOrder = 4;
    scene.add(sun);

    const horizonUniforms = { uColor: { value: new THREE.Color('#93a9c4') } };
    const horizonMaterial = new THREE.ShaderMaterial({
        vertexShader: HORIZON_VERTEX,
        fragmentShader: HORIZON_FRAGMENT,
        uniforms: horizonUniforms,
        transparent: true,
        depthWrite: false,
        side: THREE.BackSide,
        fog: false,
    });
    const horizon = new THREE.Mesh(new THREE.SphereGeometry(1, 48, 24), horizonMaterial);
    horizon.scale.setScalar(radius * 0.94);
    horizon.frustumCulled = false;
    horizon.renderOrder = 1;
    scene.add(horizon);

    // A restrained deterministic star field, visible only once the sun is down.
    // Gives the night dome depth without turning the scene into a planetarium.
    const starPositions: number[] = [];
    const starRadius = radius * 0.8;
    for (let i = 0; i < 320; i++) {
        const a = hash(i * 2.13) * Math.PI * 2;
        const y = 0.08 + hash(i * 5.71 + 9) * 0.9;
        const r = Math.sqrt(1 - y * y) * starRadius;
        starPositions.push(Math.cos(a) * r, y * starRadius, Math.sin(a) * r);
    }
    const starGeometry = new THREE.BufferGeometry();
    starGeometry.setAttribute('position', new THREE.Float32BufferAttribute(starPositions, 3));
    const starMaterial = new THREE.PointsMaterial({
        color: new THREE.Color('#dce5f4'),
        // Fixed pixel size, not world size: at a 3km shell radius an attenuated
        // point collapses to a sub-pixel speck and the night sky comes out empty.
        size: 1.7,
        sizeAttenuation: false,
        transparent: true,
        opacity: 0,
        depthWrite: false,
        fog: false,
    });
    const stars = new THREE.Points(starGeometry, starMaterial);
    stars.frustumCulled = false;
    stars.renderOrder = 3;
    scene.add(stars);

    return {
        params,
        clouds: { coverage: cloudUniforms.uCoverage, opacity: cloudUniforms.uOpacity },
        sun,
        setSun(sunDir) {
            u.sunPosition.value.copy(sunDir);
            sun.position.copy(sunDir).multiplyScalar(sunRadius);
            // The matrix is composed by hand rather than left to the renderer.
            // GodRaysEffect reparents its light source into a private scene each
            // frame and sets matrixAutoUpdate = false on the way through without
            // ever restoring it, so from the first god-rays frame onward a plain
            // position assignment stops moving anything: the sun would freeze
            // wherever it happened to be when the effect first ran, while the
            // shadows and the atmosphere carried on through the day without it.
            sun.updateMatrix();
            sun.updateMatrixWorld(true);
        },
        setLighting(daylight, dusk) {
            const c = cloudUniforms.uColor.value as THREE.Color;
            c.lerpColors(CLOUD_NIGHT, CLOUD_DAY, daylight).lerp(CLOUD_DUSK, dusk * 0.8);
            starMaterial.opacity = Math.pow(1 - daylight, 1.8) * 0.75;
            // The disc reddens and fades as it sets, and is hidden outright once
            // it is below the horizon so no god rays leak out of the ground.
            // Reddens into the dusk while keeping the red channel hot.
            sunMaterial.color.setRGB(
                SUN_COLOR.r,
                SUN_COLOR.g - dusk * 4.4,
                SUN_COLOR.b - dusk * 6.4,
            );
            sunMaterial.opacity = daylight;
            sun.visible = daylight > 0.02;
        },
        setHorizon(color) {
            (horizonUniforms.uColor.value as THREE.Color).copy(color);
        },
        applyParams,
        update(dt) {
            cloudUniforms.uTime.value += dt;
        },
        dispose() {
            scene.remove(sky);
            sky.material.dispose();
            sky.geometry.dispose();
            scene.remove(clouds);
            cloudMaterial.dispose();
            clouds.geometry.dispose();
            scene.remove(horizon);
            horizonMaterial.dispose();
            horizon.geometry.dispose();
            scene.remove(stars);
            starMaterial.dispose();
            starGeometry.dispose();
            scene.remove(sun);
            sunMaterial.dispose();
            sun.geometry.dispose();
        },
    };
}

function hash(n: number): number {
    const value = Math.sin(n * 127.1 + 311.7) * 43758.5453;
    return value - Math.floor(value);
}
